<script setup>
    import { onMounted, ref, watch } from "vue";

    const props = defineProps({
        products: {
            type: [Object, String],
            required: true,
            default: ""
        },

        imageData: {
            type: Array,
            required: false,
            default: () => { return [] }
        }
    })

    const container = ref(null),
          sticky = ref(null),
          percentage = ref(0),
          sections = ref(0),
          activeSection = ref(0)

    onMounted(() => {
        if ( props.products ) {
            sections.value = props.products.length

            // Set initial percentage (If user reloads the page while scroll position is after the component this will set the correct slide and progress bar)
            handleScroll()

            const observer = new IntersectionObserver(([entry]) => {
                if (entry.isIntersecting) {
                    startScrollListener()
                } else {
                    stopScrollListener()
                }
            })
            observer.observe(container.value)
        }
    })

    function startScrollListener() {
        window.addEventListener('scroll', handleScroll)
    }

    function stopScrollListener() {
        window.removeEventListener('scroll', handleScroll)
    }

    function handleScroll() {
        const parentRect = container.value.getBoundingClientRect()
        const stickyRect = sticky.value.getBoundingClientRect()

        const parentTop = parentRect.top
        const stickyTop = stickyRect.top

        const distanceFromTop = stickyTop - parentTop
        const parentHeight = container.value.offsetHeight - stickyRect.height

        percentage.value = (distanceFromTop / parentHeight) * 100

        if (percentage.value < 100) {
            activeSection.value = Math.floor((percentage.value / 100) * sections.value)
        } else {
            activeSection.value = props.products.length - 1
        }
    }
</script>

<template>
    <div
        v-if="products"
        ref="container"
        aria-hidden="true"
        class="bg-cintra-grey dark"
        :style="`height: calc((100vh - 9rem) * ${props.products.length})`"
    >
        <div ref="sticky" class="sticky top-32 left-0 right-0 h-[calc(theme(height.screen)_-_theme(space.32))] py-8 md:py-14">
            <div class="h-full relative">
                <div aria-hidden="true" class="absolute -rotate-90 md:rotate-0 top-auto bottom-0 md:top-1/2 left-1/2 md:left-1/2 translate-y-1/2 md:-translate-y-1/2 -translate-x-1/2 h-[90vw] md:h-full w-[5px]">
                    <div class="w-full min-h-0 max-h-full bg-cintra-orange absolute top-0 left-1/2 -translate-x-1/2 z-[1] after:absolute after:border-l-[8px] after:border-r-[8px] after:border-t-[16px] after:border-l-transparent after:border-r-transparent after:border-t-cintra-orange after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:translate-y-1/2" :style="`height: ${percentage}%`">
                    </div>
                    <div class="w-px bg-cintra-grey h-full absolute top-0 left-1/2 -translate-x-1/2"></div>
                </div>
                <transition
                    v-for="( product, index ) in products"
                    :key="index"
                    name="product"
                >
                    <div
                        v-show="activeSection === index"
                        class="w-full absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 overflow-hidden"
                    >
                        <div class="block-inner grid gap-8 md:gap-20 md:grid-cols-2 py-14 h-full">
                            <div class="product-text w-full flex items-center justify-center">
                                <div class="w-full grid gap-4 md:gap-8 lg:gap-10 max-w-lg">
                                    <div>
                                        <div class="flex items-center gap-6">
                                            <div v-if="product.product_logo" class="w-12 h-12 md:w-16 md:h-16">
                                                <picture v-if="imageData?.[index]?.product_logo">
                                                    <source
                                                        v-if="imageData[index].product_logo?.webp"
                                                        :srcset="imageData[index].product_logo?.webp"
                                                        type="image/webp"
                                                        class="w-full h-full object-contain"
                                                    >
                                                    <img
                                                        :src="imageData[index].product_logo?.default"
                                                        class="w-full h-full object-contain"
                                                    >
                                                </picture>
                                                <img v-else class="w-full h-full object-contain" :src="product.product_logo.permalink">
                                            </div>
                                            <a class="transition duration-300 hover:text-cintra-orange" :href="product.permalink">
                                                <h3 class="font-products font-normal my-0">{{ product.title }}</h3>
                                            </a>
                                        </div>
                                        <p class="font-light mb-0 md:mb-2">{{ product.description }}</p>
                                        <div>
                                            <a class="arrow-button" :aria-label='`learn more about the "${product.title}" product`' :href="product.permalink">Learn more</a>
                                        </div>
                                    </div>
                                    <div v-if="product.children && product.title === 'Assess'" class="links grid lg:grid-cols-2 gap-y-2 md:gap-y-4 lg:gap-x-14">
                                        <article v-for="service in product.children" class="text-cintra-grey flex flex-col gap-1 md:gap-3 justify-between">
                                            <div>
                                                <h4 class="my-0 text-base uppercase tracking-wide">{{ service.title }}</h4>
                                                <p class="hidden font-light text-sm my-1 md:my-2 leading-snug">{{ service.description }}</p>
                                            </div>
                                            <div>
                                                <a class="arrow-button" :aria-label='`Learn more about "${service.title}"`' :href="service.permalink">Learn more</a>
                                            </div>
                                        </article>
                                    </div>
                                </div>
                            </div>
                            <div class="hidden md:flex product-image w-full items-center justify-center">
                                <div class="w-full aspect-square flex items-center justify-center relative">
                                    <picture v-if="imageData?.[index]?.featured_image">
                                        <source
                                            v-if="imageData[index].featured_image?.webp"
                                            :srcset="imageData[index].featured_image?.webp"
                                            :alt="product.featured_image?.alt"
                                            type="image/webp"
                                            class="w-10/12 w-full h-full object-cover absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"
                                        >
                                        <img
                                            :src="imageData[index].featured_image?.default"
                                            :alt="product.featured_image?.alt"
                                            class="w-10/12 w-full h-full object-cover absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"
                                        >
                                    </picture>
                                    <img
                                        v-else-if="product?.featured_image"
                                        :src="product.featured_image.permalink"
                                        :alt="product.featured_image?.alt"
                                        class="w-10/12 w-full h-full object-cover absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2"
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </div>
    </div>
    <div class="sr-only">
        <article
            v-for="( product, index ) in products"
            :key="index"
        >
            <h3>{{ product.title }}</h3>
            <p>{{ product.description }}</p>
            <a :href="product.permalink">Learn more about the "{{ product.title }}" product</a>
            <article
                v-if="product.children"
                v-for="service in product.children"
            >
                <h4>{{ service.title }}</h4>
                <p>{{ service.description }}</p>
                <a :href="service.permalink">Learn more about "{{ service.title }}"</a>
            </article>
        </article>
    </div>
</template>
<style lang="scss" scoped>
    .product-enter-active,
    .product-leave-active,
    .product-enter-active .product-text,
    .product-leave-active .product-text,
    .product-enter-active .product-image,
    .product-leave-active .product-image {
        @apply transition;
        @apply duration-500;
    }

    .product-enter-from,
    .product-enter,
    .product-leave-to,
    .product-enter-from .product-image,
    .product-enter .product-image,
    .product-leave-to .product-image {
        @apply opacity-0;
    }

    .product-enter-to,
    .product-enter-to .product-image {
        @apply opacity-100;
    }

    .product-enter-from .product-text,
    .product-enter .product-text,
    .product-leave-to .product-text {
        @apply opacity-0;
        @apply motion-safe:-translate-x-1/3;
    }

    .product-enter-to .product-text {
        @apply opacity-100;
        @apply translate-x-0;
    }
</style>
